/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

// Matches Hygraph's default CDNs and Epic's CNAME aliases (staging + prod), so that feed
// API responses that return prod URLs are still recognised in staging/local dev.
const HYGRAPH_HOST_PATTERNS = [/\.graphassets\.com$/, /(^|\.)cms-assets(-[\w-]+)?\.unrealengine\.com$/];

/**
 * Determines if a given src URL is a hygraph asset
 * @param hygraphMediaDomain
 * @param src
 * @returns boolean
 */
export const isHygraphImageSrc = (hygraphMediaDomain: string, src: string): boolean => {
	if (!src) return false;
	if (hygraphMediaDomain && src.includes(hygraphMediaDomain)) return true;
	// src may be a full URL or a bare hostname (getHygraphImageIds calls this with parts[2])
	let host = src;
	try {
		host = new URL(src).hostname;
	} catch {
		// leave host as the raw src (likely already a hostname)
	}
	return HYGRAPH_HOST_PATTERNS.some((re) => re.test(host));
};

interface HygraphImageIds {
	assetId: string;
	environmentId?: string;
}

/**
 * Attempts to parse hygraph asset URL and return the assetId, environmentId
 */
export const getHygraphImageIds = (hygraphMediaDomain: string, src: string): HygraphImageIds => {
	let assetId = "";
	let environmentId = "";

	if (!src) {
		return { assetId, environmentId };
	}

	// strip query string before parsing so suffixes like ?width=N don't mask the trailing id segment
	const pathOnly = src.split("?")[0] || src;
	const parts = pathOnly.split("/");

	if (parts.length && parts[2] && isHygraphImageSrc(hygraphMediaDomain, parts[2]) && parts.length > 3) {
		assetId = parts[3];

		//new system
		if (parts.length >= 5) {
			// remove output= resize=x: parts
			const cleanedIds = parts
				.slice(4)
				.filter((el) => el && !el.includes(":") && !el.includes("=") && el !== "auto_image");
			if (cleanedIds.length > 0) environmentId = cleanedIds[0];
		}
	}

	return { assetId, environmentId };
};
